import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

import sanitize from 'lib/HtmlSanitizer'
import { FilterAttributes, generateAPIPath, MultiSelectValue } from '../Helper'
import filterDataAPI from 'api/ApiActions/TextAnalysis/FilterData'

const useFilterData = ({ apiSource }: Pick<FilterAttributes, 'apiSource'>) => {
  const [searchParams] = useSearchParams()
  const [options, setOptions] = useState<MultiSelectValue[]>([])

  // Memoize the project_id and owner_id to prevent unnecessary re-renders
  const project_id = useMemo(
    () => searchParams.get('project_id') || '',
    [searchParams],
  )
  const owner_id = useMemo(
    () => searchParams.get('owner_id') || '',
    [searchParams],
  )

  // Memoize the fetchData function to prevent re-creation on each render
  const fetchData = useCallback(async () => {
    try {
      const { data } = await filterDataAPI.getFilterData(
        generateAPIPath(apiSource, project_id, owner_id),
        { filters: { type: ['EssayQuestion', 'SingleLineQuestion'] } },
      )
      const sanitizedOptions = data.data.map(
        ({ question, id }: { question: string; id: number }) => ({
          id,
          label: sanitize(question).__html,
        }),
      )

      setOptions(sanitizedOptions)
    } catch (error) {
      console.error('Failed to fetch filter data', error)
    }
  }, [apiSource, project_id, owner_id])

  // Fetch data when apiSource, project_id, or owner_id changes
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return options
}

const useIcons = () => {
  // Memoize the icon components to avoid unnecessary re-renders
  const icon = useMemo(() => <CheckBoxOutlineBlankIcon fontSize="small" />, [])
  const checkedIcon = useMemo(() => <CheckBoxIcon fontSize="small" />, [])

  return { icon, checkedIcon }
}

export { useIcons, useFilterData }
